import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";
import {
  createTrackLinkDocument,
  MusicLinksResult,
  TrackLinkData,
  UserInfo,
} from "../../services/AdminService";
import {
  sortedStates,
  sortedRegions,
  sortedGenres,
} from "../../utils/StaticFormData";
import { IoCloudUploadOutline } from "react-icons/io5";

interface CreateTrackFormProps {
  linkResults: MusicLinksResult;
  usersList: UserInfo[];
  onSuccess: () => void;
}

export const CreateTrackForm = ({
  linkResults,
  usersList,
  onSuccess,
}: CreateTrackFormProps) => {
  const { showNotification } = useNotification();
  const [isCreating, setIsCreating] = useState(false);
  const [featureArtistsInput, setFeatureArtistsInput] = useState("");
  const [moodTagsInput, setMoodTagsInput] = useState("");

  const [formData, setFormData] = useState<Omit<TrackLinkData, "releaseDate">>({
    artistName: "",
    coverArtUrl: "",
    featureArtists: [],
    genre: "",
    moodTags: [],
    platformLinks: {},
    regionID: 0,
    stateID: 0,
    releaseType: "Single",
    trackTitle: "",
    userId: "",
  });

  useEffect(() => {
    let normalizedType = linkResults.type || "Single";
    const typeLower = normalizedType.toLowerCase();
    if (typeLower === "album" || typeLower === "ep") {
      normalizedType = "Album/EP";
    }

    const detectedGenre =
      linkResults.genres && linkResults.genres.length > 0
        ? linkResults.genres[0]
        : "";

    setFormData((prev) => ({
      ...prev,
      trackTitle: linkResults.name || "",
      coverArtUrl: linkResults.coverArtUrl || "",
      platformLinks: linkResults.links,
      releaseType: normalizedType,
      genre: detectedGenre || prev.genre,
    }));
  }, [linkResults]);

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleArtistSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUid = e.target.value;
    const selectedUser = usersList.find((u) => u.userId === selectedUid);

    if (selectedUser) {
      setFormData((prev) => ({
        ...prev,
        userId: selectedUid,
        artistName: selectedUser.artistName,
        stateID: selectedUser.stateID || 0,
        regionID: selectedUser.regionID || 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        userId: "",
        artistName: "",
        stateID: 0,
        regionID: 0,
      }));
    }
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStateID = parseInt(e.target.value);
    setFormData((prev) => ({ ...prev, stateID: newStateID, regionID: 0 }));
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, regionID: parseInt(e.target.value) }));
  };

  const handleCreateDocument = async () => {
    if (!formData.userId || !formData.artistName) {
      showNotification("Please select an Artist/User first.");
      return;
    }

    setIsCreating(true);
    try {
      const finalData = {
        ...formData,
        releaseDate: new Date(),
        featureArtists: featureArtistsInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        moodTags: moodTagsInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };

      await createTrackLinkDocument(finalData);
      showNotification("Track Link Document Created Successfully!");

      setFeatureArtistsInput("");
      setMoodTagsInput("");
      onSuccess();
    } catch (error) {
      console.error(error);
      showNotification("Error creating document. Check console.");
    } finally {
      setIsCreating(false);
    }
  };

  const availableRegions = sortedRegions.filter(
    (region) => region.stateID === formData.stateID,
  );

  return (
    <div
      className="admin-section"
      style={{ marginTop: 24, animation: "fadeIn 0.5s" }}
    >
      <h3 className="section-header">Create Track Document</h3>

      <div className="form-grid-2">
        <div className="form-group">
          <label>Track Title</label>
          <input
            name="trackTitle"
            className="admin-input"
            value={formData.trackTitle}
            onChange={handleFormChange}
          />
        </div>

        <div className="form-group">
          <label>Artist Name (Select User)</label>
          <select
            className="admin-select"
            value={formData.userId}
            onChange={handleArtistSelect}
          >
            <option value="">Select Artist...</option>
            {usersList.map((user) => (
              <option key={user.userId} value={user.userId}>
                {user.artistName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Feature Artists</label>
          <input
            className="admin-input"
            value={featureArtistsInput}
            onChange={(e) => setFeatureArtistsInput(e.target.value)}
            placeholder="e.g. Drake"
          />
        </div>

        <div className="form-group">
          <label>Genre</label>
          <input
            name="genre"
            list="genre-options"
            className="admin-input"
            value={formData.genre}
            onChange={handleFormChange}
            placeholder="Select or type genre..."
          />
          <datalist id="genre-options">
            {sortedGenres.map((g) => (
              <option key={g.genreID} value={g.genreName} />
            ))}
          </datalist>
        </div>

        <div className="form-group">
          <label>Mood Tags</label>
          <input
            className="admin-input"
            value={moodTagsInput}
            onChange={(e) => setMoodTagsInput(e.target.value)}
            placeholder="e.g. Hype, Gym"
          />
        </div>

        <div className="form-group">
          <label>Release Type</label>
          <select
            name="releaseType"
            className="admin-select"
            value={formData.releaseType}
            onChange={handleFormChange}
          >
            <option value="Single">Single</option>
            <option value="Album/EP">Album/EP</option>
          </select>
        </div>

        <div className="form-group">
          <label>User ID</label>
          <input
            name="userId"
            className="admin-input read-only"
            value={formData.userId}
            readOnly
          />
        </div>

        <div className="form-group">
          <label>State</label>
          <select
            className="admin-select"
            value={formData.stateID}
            onChange={handleStateChange}
          >
            <option value={0}>Select State</option>
            {sortedStates.map((s) => (
              <option key={s.stateID} value={s.stateID}>
                {s.stateName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Region</label>
          <select
            className="admin-select"
            value={formData.regionID}
            onChange={handleRegionChange}
            disabled={!formData.stateID}
          >
            <option value={0}>Select Region</option>
            {availableRegions.map((r) => (
              <option key={r.regionID} value={r.regionID}>
                {r.regionName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group full-width">
          <label>Cover Art URL</label>
          <input
            name="coverArtUrl"
            className="admin-input"
            value={formData.coverArtUrl}
            onChange={handleFormChange}
          />
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <label
          className="link-label"
          style={{ marginBottom: 10, display: "block" }}
        >
          Platform Links
        </label>
        <div className="form-grid-2">
          {Object.entries(formData.platformLinks).map(([key, val]) => (
            <div key={key} className="form-group">
              <label style={{ fontSize: 11, color: "#aaa" }}>{key}</label>
              <input
                className="admin-input read-only"
                value={val as string}
                readOnly
              />
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 32 }}>
        <button
          className="btn-primary btn-full"
          onClick={handleCreateDocument}
          disabled={isCreating}
        >
          {isCreating ? (
            "Creating..."
          ) : (
            <>
              <IoCloudUploadOutline size={20} style={{ marginRight: 8 }} />
              Create Document
            </>
          )}
        </button>
      </div>
    </div>
  );
};
